"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { usePostHogReady } from "@/components/providers/posthog-provider";
import { defaultLocale, type Locale } from "@/lib/i18n/config";
import { isValidLocale } from "@/lib/i18n/locale";

/**
 * Self-contained 404 recovery experience shared by:
 *  - `src/app/not-found.tsx`            (unmatched URLs, e.g. /en/ac, /foo)
 *  - `src/app/[locale]/not-found.tsx`   (programmatic notFound() inside a locale route)
 *
 * It cannot read route params (Next.js forbids params in not-found.tsx), so it
 * derives the active locale from the pathname on the client and falls back to the
 * default locale for the first paint. Copy is bilingual: the detected locale leads,
 * the other language follows as a muted secondary line.
 */

type Copy = {
	badge: string;
	heading: string;
	subheading: string;
	searchLabel: string;
	searchPlaceholder: string;
	searchCta: string;
	popularLabel: string;
	homeCta: string;
	helpPrefix: string;
	helpLink: string;
	switchLabel: string;
};

const COPY: Record<Locale, Copy> = {
	en: {
		badge: "Page not found",
		heading: "Looks like this page clocked out early.",
		subheading:
			"We couldn't find what you were looking for — but your next remote role is still out there. Try a search or pick up one of the trails below.",
		searchLabel: "Search remote jobs",
		searchPlaceholder: "Job title, company, or skill…",
		searchCta: "Search jobs",
		popularLabel: "Popular destinations",
		homeCta: "Back to homepage",
		helpPrefix: "Think something's broken?",
		helpLink: "Let us know",
		switchLabel: "Lihat dalam Bahasa Indonesia",
	},
	id: {
		badge: "Halaman tidak ditemukan",
		heading: "Sepertinya halaman ini sedang cuti.",
		subheading:
			"Kami tidak menemukan halaman yang kamu cari — tapi peluang kerja remote berikutnya masih menanti. Coba cari, atau pilih salah satu tautan di bawah.",
		searchLabel: "Cari lowongan remote",
		searchPlaceholder: "Posisi, perusahaan, atau skill…",
		searchCta: "Cari lowongan",
		popularLabel: "Tujuan populer",
		homeCta: "Kembali ke beranda",
		helpPrefix: "Merasa ada yang rusak?",
		helpLink: "Beri tahu kami",
		switchLabel: "View in English",
	},
};

type PopularLink = { key: string; en: string; id: string; path: string };

// Every path here must resolve. This list is the recovery route off a dead end,
// so a broken entry sends a visitor from one 404 straight into another — which
// is exactly what happened until 2026-09-03: the first and most prominent entry
// pointed at `remote-jobs`, a path with no index page (only
// `remote-jobs/[category]`). It was removed rather than repointed because
// `categories` below is the same destination — it is that family's hub, and
// middleware 301s `/categories/[slug]` → `/remote-jobs/[slug]`.
const POPULAR_LINKS: PopularLink[] = [
	{ key: "jobs", en: "All jobs", id: "Semua lowongan", path: "jobs" },
	{ key: "categories", en: "Categories", id: "Kategori", path: "categories" },
	{
		key: "salary",
		en: "Salary guide",
		id: "Panduan gaji",
		path: "salary-guide",
	},
	{ key: "companies", en: "Companies", id: "Perusahaan", path: "companies" },
	{ key: "blog", en: "Career blog", id: "Blog karier", path: "blog" },
];

function detectLocale(): Locale {
	if (typeof window === "undefined") return defaultLocale;
	const first = window.location.pathname.split("/").filter(Boolean)[0];
	return isValidLocale(first) ? first : defaultLocale;
}

export function NotFoundContent() {
	const router = useRouter();
	const posthog = usePostHog();
	const isPostHogReady = usePostHogReady();
	// First paint must match the server render (default locale) to avoid a hydration
	// mismatch; we refine to the real locale in an effect right after mount.
	const [locale, setLocale] = useState<Locale>(defaultLocale);
	const [query, setQuery] = useState("");

	useEffect(() => {
		setLocale(detectLocale());
	}, []);

	// Single render call site for both not-found.tsx boundaries (root + [locale]),
	// so this is the one place a real 404 is checkable in telemetry — before this,
	// no event carried an HTTP status at all (see
	// docs/plans/archive/telemetry-vs-reality-gap-2026-07-23.md W1-14).
	useEffect(() => {
		if (!isPostHogReady) return;
		posthog?.capture("http_error_page_rendered", {
			http_status: 404,
			path: window.location.pathname,
		});
	}, [isPostHogReady, posthog]);

	const t = COPY[locale];
	const otherLocale: Locale = locale === "en" ? "id" : "en";

	function onSearch(e: React.FormEvent) {
		e.preventDefault();
		const q = query.trim();
		const target = q
			? `/${locale}/jobs?q=${encodeURIComponent(q)}`
			: `/${locale}/jobs`;
		router.push(target);
	}

	return (
		// `data-testid` is the stable handle `scripts/ops/link-audit.ts` uses to
		// recognise a soft 404 — this body rendered under HTTP 200. Matching on
		// the copy instead would break the moment the copy is reworded, and the
		// copy here is localised, so there is no single string to match on.
		<main
			data-testid="not-found-content"
			className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-5 py-16"
		>
			{/* Soft brand wash — decorative, sits behind everything */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_50%_0%,color-mix(in_srgb,var(--primary)_12%,transparent),transparent_70%)]"
			/>

			{/* Brand wordmark */}
			<Link
				href={`/${locale}`}
				className="absolute left-1/2 top-8 -translate-x-1/2 rounded-md text-sm font-semibold tracking-tight text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
			>
				Loker<span className="text-primary">Dollar</span>
			</Link>

			<div className="mx-auto w-full max-w-xl text-center">
				{/* Oversized status numeral — decorative, gradient brand fill */}
				<p
					aria-hidden="true"
					className="select-none bg-gradient-to-b from-foreground/[0.08] to-foreground/[0.02] bg-clip-text font-black leading-none tracking-[-0.05em] text-transparent"
					style={{ fontSize: "clamp(7rem, 26vw, 14rem)" }}
				>
					404
				</p>

				{/* Status badge */}
				<div className="-mt-4 mb-6 flex justify-center sm:-mt-8">
					<span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-4 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur">
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={2}
							className="h-3.5 w-3.5 text-primary"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="m21 21-4.3-4.3M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z"
							/>
						</svg>
						{t.badge}
					</span>
				</div>

				<h1 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
					{t.heading}
				</h1>
				<p className="mx-auto mt-4 max-w-md text-pretty text-base leading-relaxed text-muted-foreground">
					{t.subheading}
				</p>

				{/* Search — the primary recovery path */}
				<search className="mt-8 block">
					<form
						onSubmit={onSearch}
						className="mx-auto flex max-w-md items-center overflow-hidden rounded-2xl border border-border bg-card/80 shadow-sm ring-offset-background transition-colors focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
					>
						<label htmlFor="not-found-search" className="sr-only">
							{t.searchLabel}
						</label>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={2}
							className="pointer-events-none ml-4 h-4 w-4 shrink-0 text-muted-foreground"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="m21 21-4.3-4.3M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z"
							/>
						</svg>
						<input
							id="not-found-search"
							name="q"
							type="search"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={t.searchPlaceholder}
							autoComplete="off"
							enterKeyHint="search"
							className="h-12 min-w-0 flex-1 bg-transparent px-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none"
						/>
						<button
							type="submit"
							className="m-1.5 inline-flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
						>
							{t.searchCta}
						</button>
					</form>
				</search>

				{/* Popular destinations */}
				<div className="mt-10">
					<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/70">
						{t.popularLabel}
					</p>
					<div className="mt-4 flex flex-wrap justify-center gap-2.5">
						{POPULAR_LINKS.map((link) => (
							<Link
								key={link.key}
								href={`/${locale}/${link.path}`}
								className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-card/60 px-4 py-2 text-sm font-medium text-foreground transition-colors duration-150 hover:border-primary/40 hover:bg-accent hover:text-accent-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
							>
								{locale === "en" ? link.en : link.id}
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={2}
									className="h-3.5 w-3.5 text-muted-foreground"
									aria-hidden="true"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M9 5l7 7-7 7"
									/>
								</svg>
							</Link>
						))}
					</div>
				</div>

				{/* Primary home CTA */}
				<div className="mt-10">
					<Link
						href={`/${locale}`}
						className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-border px-6 py-3 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:bg-muted/60 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
					>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={2}
							className="h-4 w-4"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75"
							/>
						</svg>
						{t.homeCta}
					</Link>
				</div>

				{/* Footer helpers */}
				<div className="mt-10 flex flex-col items-center gap-3 text-xs text-muted-foreground">
					<p>
						{t.helpPrefix}{" "}
						<a
							href="mailto:hello@lokerdollar.com?subject=Broken%20link"
							className="font-medium text-primary underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
						>
							{t.helpLink}
						</a>
					</p>
					<Link
						href={`/${otherLocale}`}
						className="rounded-md font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
					>
						{t.switchLabel}
					</Link>
				</div>
			</div>
		</main>
	);
}
